import { useState, useEffect, useRef, useMemo } from "react";
import {
  saveTasks,
  loadTasks,
  uploadAttachment,
  deleteAttachment
} from "../api/povinnostiApi";
import {
  normalizeCompany,
  loadCompanyList,
  saveCompanyList,
  mergeCompanyLists,
  normalizeTaskName,
  loadTaskList,
  saveTaskList,
  mergeTaskLists,
} from "../utils/companyUtils";

export function usePovinnosti() {
  // Pomocná funkcia na formátovanie dátumu na dd.mm.rrrr
  function formatDateDMY(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('sk-SK');
  }

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userPin, setUserPin] = useState("5085");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [registerMode, setRegisterMode] = useState(false);
  
  // Formulár
  const [formText, setFormText] = useState("");
  const [autocompleteText, setAutocompleteText] = useState("");
  const [showTaskSuggestions, setShowTaskSuggestions] = useState(false);
  const povinnostInputRef = useRef(null);
  const [company, setCompany] = useState("");
  const [deadline, setDeadline] = useState(new Date().toISOString().slice(0, 10));
  const [deadlineTime, setDeadlineTime] = useState("");
  const [repeatInterval, setRepeatInterval] = useState("monthly");
  const [estimatedTime, setEstimatedTime] = useState(60);
  const [subtasksInput, setSubtasksInput] = useState("");
  const [formFiles, setFormFiles] = useState([]);

  // Hromadné úpravy
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showBulkEditText, setShowBulkEditText] = useState(false);
  const [showBulkAddSubtask, setShowBulkAddSubtask] = useState(false);
  const [bulkEditDate, setBulkEditDate] = useState("");
  const [bulkEditText, setBulkEditText] = useState("");
  const [bulkSubtaskText, setBulkSubtaskText] = useState("");
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  const [editId, setEditId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // --- ZOZNAM FIRIEM ---
  const [companyList, setCompanyList] = useState(() => loadCompanyList());

  // Merge companies from loaded tasks into the list
  useEffect(() => {
    if (tasks.length === 0) return;
    const fromTasks = tasks
      .filter(t => t.company && !String(t.id).includes("_hist_"))
      .map(t => normalizeCompany(t.company))
      .filter(Boolean);
    if (fromTasks.length === 0) return;
    setCompanyList(prev => {
      const merged = mergeCompanyLists(prev, fromTasks);
      if (merged.length !== prev.length) saveCompanyList(merged);
      return merged;
    });
  }, [tasks]);

  const addToCompanyList = (name) => {
    const normalized = normalizeCompany(name);
    if (!normalized) return;
    setCompanyList(prev => {
      if (prev.includes(normalized)) return prev;
      const updated = mergeCompanyLists(prev, [normalized]);
      saveCompanyList(updated);
      return updated;
    });
  };

  // --- ZOZNAM POVINNOSTÍ ---
  const [taskList, setTaskList] = useState(() => loadTaskList());

  // Merge task names from loaded tasks into the list
  useEffect(() => {
    if (tasks.length === 0) return;
    const fromTasks = tasks
      .filter(t => t.text && !String(t.id).includes("_hist_"))
      .map(t => normalizeTaskName(t.text))
      .filter(Boolean);
    if (fromTasks.length === 0) return;
    setTaskList(prev => {
      const merged = mergeTaskLists(prev, fromTasks);
      if (merged.length !== prev.length) saveTaskList(merged);
      return merged;
    });
  }, [tasks]);

  const addToTaskList = (name) => {
    const normalized = normalizeTaskName(name);
    if (!normalized) return;
    setTaskList(prev => {
      if (prev.includes(normalized)) return prev;
      const updated = mergeTaskLists(prev, [normalized]);
      saveTaskList(updated);
      return updated;
    });
  };

  // --- API HANDLERY ---
  const handleLoadTasks = async (pin) => {
    setLoading(true);
    setError("");
    try {
      const loaded = await loadTasks(pin);
      setTasks(loaded || []);
    } catch (e) {
      setError("Chyba načítania úloh");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTasks = async (pin, newTasks) => {
    // Caller already calls setTasks directly; we just persist to server
    await saveTasks(pin, newTasks);
  };

  const handlePinLogin = async (pin) => {
    if (!/^\d{4}$/.test(pin)) {
      setPinError("PIN musí mať presne 4 číslice");
      return false;
    }
    setIsAuthenticated(true);
    setUserPin(pin);
    setPinError("");
    setPinInput("");
    await handleLoadTasks(pin);
    return true;
  };

  // Hromadné akcie (opravené na funkcionálny update)
  const handleBulkAddSubtask = async () => {
    if (!bulkSubtaskText.trim() || selectedTaskIds.length === 0) return;
    const lines = bulkSubtaskText.split('\n').map(s => s.trim()).filter(Boolean);
    
    setTasks(prev => {
      const updated = prev.map(t => {
        if (!selectedTaskIds.includes(t.id)) return t;
        const newST = lines.map(text => ({
          id: `st-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          text,
          completed: false,
          trackedTime: 0
        }));
        return { ...t, subtasks: [...(t.subtasks || []), ...newST] };
      });
      saveTasks(userPin, updated);
      return updated;
    });
    setBulkSubtaskText("");
    setShowBulkAddSubtask(false);
  };

  const handleAddTasks = async (newTasks) => {
    const extraTasks = [];
    newTasks.forEach(task => {
      if (/registr[áa]cia na dph/i.test(task.text || "")) {
        const baseDeadline = task.deadline ? new Date(task.deadline) : new Date();
        const linkedDeadline = new Date(baseDeadline);
        linkedDeadline.setDate(linkedDeadline.getDate() + 30);
        extraTasks.push({
          id: Date.now().toString() + Math.random().toString(36).slice(2),
          text: "Oznámenie čísla účtu daňovému úradu",
          company: task.company || "",
          deadline: linkedDeadline.toISOString().slice(0, 10),
          interval: "none",
          estimatedTime: "",
          subtasks: [],
          completed: false,
        });
      }
    });
    setTasks(prev => {
      const updated = [...prev, ...newTasks, ...extraTasks];
      saveTasks(userPin, updated);
      return updated;
    });
  };

  const saveEdit = async (id) => {
    if (!id) return;
    setTasks(prev => {
      const updated = prev.map(t => t.id === id ? { ...t, ...editValues } : t);
      saveTasks(userPin, updated);
      return updated;
    });
    setEditId(null);
    setEditValues({});
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditValues({});
  };

  // Načítanie pri štarte, ak je PIN
  const uniqueTaskTemplatesFiltered = useMemo(() => {
    if (!autocompleteText.trim()) return [];
    const norm = autocompleteText.toLowerCase();
    return Array.from(new Set(tasks.map(t => t.text).filter(Boolean)))
      .filter(t => t.toLowerCase().includes(norm))
      .slice(0, 8);
  }, [tasks, autocompleteText]);

  useEffect(() => {
    if (isAuthenticated && userPin) {
      handleLoadTasks(userPin);
    }
  }, [isAuthenticated, userPin]);

  return {
    tasks, setTasks, loading, error, userPin, setUserPin,
    isAuthenticated, setIsAuthenticated, pinInput, setPinInput,
    pinError, setPinError, registerMode, setRegisterMode,
    formText, setFormText, company, setCompany, deadline, setDeadline, deadlineTime, setDeadlineTime,
    repeatInterval, setRepeatInterval, estimatedTime, setEstimatedTime,
    subtasksInput, setSubtasksInput, formFiles, setFormFiles,
    handleSaveTasks, handleLoadTasks, handlePinLogin, handleAddTasks,
    autocompleteText, setAutocompleteText, showTaskSuggestions, setShowTaskSuggestions,
    povinnostInputRef, uniqueTaskTemplatesFiltered,
    showBulkEdit, setShowBulkEdit, showBulkEditText, setShowBulkEditText,
    showBulkAddSubtask, setShowBulkAddSubtask, bulkEditDate, setBulkEditDate,
    bulkEditText, setBulkEditText, bulkSubtaskText, setBulkSubtaskText,
    selectedTaskIds, setSelectedTaskIds, editId, setEditId, editValues, setEditValues,
    handleBulkAddSubtask, saveEdit, cancelEdit,
    formatDateDMY,
    companyList, addToCompanyList,
    taskList, addToTaskList,
  };
}